// components/course/CourseSalesSections.jsx
// The narrative sales sections for the video course track, rendered around the
// existing "My Courses" / "Pick your track" pricing blocks on /courses.
//
// Default export renders the top of the funnel:
//   hero → tool marquee → "you don't need these" → "is this you" → structure.
// Named exports render the bottom of the funnel (placed by CoursesPage after
// the pricing grid): RefundPolicySection, FinalCtaSection.
//
// It shows NO price amounts and fetches NO data. Every CTA is a plain anchor
// that scrolls to the existing pricing block (#tracks) on the same page. It
// imports nothing from coursesApi / workbooksApi / payment.
//
// Media that hasn't been produced yet renders as MediaPlaceholder with a label
// describing the asset, so the real image/motion graphic drops in later with
// zero layout changes.
//
// Editorial treatment: numbered section kickers, oversized Fraunces display
// type, alternating navy/cream color blocking, ghost numerals, a slow tool
// marquee. Brand-locked palette, NO shadows, no em/en dashes in copy.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { courseSalesContent as CONTENT } from "../../data/courseSalesContent";
import MediaPlaceholder from "../MediaPlaceholder";

const C = {
  cream: "#F5F2EC",
  navy: "#1a1a2e",
  blue: "#2563EB",
  orange: "#EA580C",
  green: "#16A34A",
  gold: "#D97706",
  red: "#DC2626",
  white: "#FFFFFF",
  ink: "#1a1a2e",
  muted: "rgba(26,26,46,0.62)",
  border: "rgba(26,26,46,0.14)",
  creamText: "rgba(245,242,236,0.72)",
  creamBorder: "rgba(245,242,236,0.18)",
};

const serif = "'Fraunces', Georgia, serif";
const sans = "'Plus Jakarta Sans', system-ui, sans-serif";
const mono = "'JetBrains Mono', monospace";

// Numbered editorial kicker: "01 · A quick check" in mono, with a rule.
function Kicker({ children, on = "light", color = C.blue }) {
  const line = on === "light" ? C.border : C.creamBorder;
  const text = on === "light" ? color : "rgba(245,242,236,0.85)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
      <span
        style={{
          fontFamily: mono,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: text,
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </span>
      <span style={{ flex: 1, height: 1, background: line }} />
    </div>
  );
}

function Pill({ children, bg = C.blue, fg = "#fff" }) {
  return (
    <span
      style={{
        display: "inline-block",
        fontFamily: mono,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: fg,
        background: bg,
        padding: "8px 18px",
        borderRadius: 100,
      }}
    >
      {children}
    </span>
  );
}

// A brand-styled primary CTA. Always a plain anchor that scrolls to the
// existing pricing block (#tracks) on the same page. It never triggers
// checkout or navigation.
function PrimaryButton({ children, color = C.blue }) {
  return (
    <a
      href="#tracks"
      className="csp-btn"
      style={{
        display: "inline-block",
        fontFamily: mono,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: "0.04em",
        color: "#fff",
        background: color,
        border: "none",
        padding: "16px 34px",
        borderRadius: 4,
        cursor: "pointer",
        textDecoration: "none",
      }}
    >
      {children}
    </a>
  );
}

function CheckItem({ children, dark = false }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
      <span
        style={{
          flexShrink: 0,
          marginTop: 3,
          width: 20,
          height: 20,
          borderRadius: 4,
          border: `1.5px solid ${dark ? "rgba(245,242,236,0.4)" : C.blue}`,
          color: dark ? "rgba(245,242,236,0.85)" : C.blue,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        →
      </span>
      <span style={{ fontSize: 16, fontWeight: 400, lineHeight: 1.6, color: dark ? C.creamText : C.muted }}>
        {children}
      </span>
    </div>
  );
}

// Fake browser chrome around a media placeholder, so motion-graphic stand-ins
// read as "a thing you will watch" instead of an empty box.
function MediaFrame({ label, dark = false, aspect = "16/9" }) {
  const border = dark ? C.creamBorder : C.border;
  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: 10,
        overflow: "hidden",
        background: dark ? "rgba(245,242,236,0.03)" : C.white,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "10px 14px",
          borderBottom: `1px solid ${border}`,
        }}
      >
        {[C.red, C.gold, C.green].map((dot) => (
          <span key={dot} style={{ width: 9, height: 9, borderRadius: "50%", background: dot, opacity: 0.85 }} />
        ))}
        <span
          style={{
            marginLeft: 8,
            fontFamily: mono,
            fontSize: 10,
            letterSpacing: "0.08em",
            color: dark ? "rgba(245,242,236,0.4)" : "rgba(26,26,46,0.4)",
          }}
        >
          scriptedcad
        </span>
      </div>
      <MediaPlaceholder label={label} aspect={aspect} dark={dark} style={{ border: "none" }} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Top of the funnel: hero → marquee → don't need → is this you →
   structure. Rendered above the pricing grid.                         */
/* ------------------------------------------------------------------ */

export default function CourseSalesSections() {
  // Click-to-zoom lightbox for the structure-card videos: {src, label} | null.
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    // <body> is the page scroller in this app; lock it while the overlay is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [zoom]);

  return (
    <div style={{ fontFamily: sans }}>
      {/* 1. HERO. Split layout: story left, motion-graphic stand-in right. */}
      <section style={{ background: C.navy, overflow: "hidden" }}>
        <div
          className={`csp-wrap csp-hero${CONTENT.hero.showMedia ? "" : " csp-hero--solo"}`}
          style={{ padding: "96px 24px 84px" }}
        >
          <div className="csp-fade">
            <div style={{ marginBottom: 26 }}>
              <span
                style={{
                  fontFamily: mono,
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: "rgba(245,242,236,0.55)",
                  border: `1px solid ${C.creamBorder}`,
                  padding: "8px 18px",
                  borderRadius: 100,
                }}
              >
                {CONTENT.hero.eyebrow}
              </span>
            </div>
            <h1
              style={{
                margin: "0 0 22px",
                fontFamily: serif,
                fontWeight: 700,
                fontSize: "clamp(40px, 5.6vw, 68px)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em",
                color: "#fff",
              }}
            >
              {CONTENT.hero.headline}
            </h1>
            <p style={{ margin: "0 0 36px", maxWidth: 480, fontSize: 19, fontWeight: 400, lineHeight: 1.6, color: C.creamText }}>
              {CONTENT.hero.sub}
            </p>
            <div style={{ marginBottom: 28 }}>
              <PrimaryButton color={C.blue}>{CONTENT.hero.cta} →</PrimaryButton>
            </div>
            <p style={{ margin: 0, fontFamily: mono, fontSize: 12.5, letterSpacing: "0.02em", color: "rgba(245,242,236,0.5)" }}>
              {CONTENT.hero.socialProof}
            </p>
          </div>
          {CONTENT.hero.showMedia && (
            <div className="csp-fade csp-fade-2">
              <MediaFrame label={CONTENT.hero.mediaLabel} dark aspect="4/3" />
            </div>
          )}
        </div>

        {/* Tool marquee: slow, seamless, pauses for reduced motion. */}
        <div className="csp-marquee" style={{ borderTop: `1px solid ${C.creamBorder}` }} aria-hidden="true">
          <div className="csp-marquee-inner">
            {[0, 1].map((copy) => (
              <div key={copy} style={{ display: "flex", flexShrink: 0 }}>
                {CONTENT.marquee.map((item) => (
                  <span
                    key={item}
                    style={{
                      fontFamily: mono,
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: "rgba(245,242,236,0.45)",
                      padding: "18px 0 18px 32px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item}
                    <span style={{ color: C.gold, marginLeft: 32 }}>✦</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. YOU DON'T NEED THESE TO START. Cream block, struck-through
          prerequisite cards with a slight paper-scatter tilt. */}
      <section style={{ background: C.cream }}>
        <div className="csp-wrap" style={{ padding: "92px 24px" }}>
          <Kicker>{CONTENT.dontNeed.kicker}</Kicker>
          <h2
            style={{
              margin: "0 0 14px",
              fontFamily: serif,
              fontWeight: 700,
              fontSize: "clamp(32px, 4.6vw, 52px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.05,
              color: C.ink,
            }}
          >
            You <span style={{ color: C.red, textDecoration: "line-through", textDecorationThickness: 3 }}>don't need</span> these to start
          </h2>
          <p style={{ margin: "0 0 46px", maxWidth: 560, fontSize: 17, lineHeight: 1.65, color: C.muted }}>
            {CONTENT.dontNeed.sub}
          </p>

          <div className="csp-dont-grid">
            {CONTENT.dontNeed.items.map((item) => (
              <div key={item.strike} className="csp-dont-card">
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
                  <span
                    style={{
                      flexShrink: 0,
                      marginTop: 2,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "rgba(220,38,38,0.1)",
                      color: C.red,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    ✕
                  </span>
                  <span
                    style={{
                      fontFamily: serif,
                      fontSize: 20,
                      fontWeight: 700,
                      lineHeight: 1.25,
                      color: C.ink,
                      textDecoration: "line-through",
                      textDecorationColor: "rgba(220,38,38,0.55)",
                      textDecorationThickness: 2,
                    }}
                  >
                    {item.strike}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: C.muted }}>{item.note}</p>
              </div>
            ))}

            {/* Closing card: the one thing you DO need, in navy. */}
            <div className="csp-dont-card csp-dont-card--yes">
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: C.green,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  marginBottom: 14,
                }}
              >
                ✓
              </span>
              <p style={{ margin: 0, fontFamily: serif, fontSize: 19, fontWeight: 600, lineHeight: 1.5, color: "#fff" }}>
                {CONTENT.dontNeed.allYouNeed}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHO THIS IS FOR. Navy block: checklist left, motion stand-in right. */}
      <section style={{ background: C.navy }}>
        <div className="csp-wrap" style={{ padding: "92px 24px" }}>
          <Kicker on="dark">{CONTENT.isThisYou.kicker}</Kicker>
          <div style={{ marginBottom: 24 }}>
            <Pill bg={C.gold}>{CONTENT.isThisYou.heading}</Pill>
          </div>
          <div className={`csp-split${CONTENT.isThisYou.showMedia ? "" : " csp-split--solo"}`}>
            <div>
              <h3 style={{ margin: "0 0 26px", fontFamily: serif, fontWeight: 700, fontSize: 28, color: "#fff" }}>
                {CONTENT.isThisYou.subheading}
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {CONTENT.isThisYou.points.map((p) => (
                  <CheckItem key={p} dark>
                    {p}
                  </CheckItem>
                ))}
              </div>
              <p
                style={{
                  margin: "30px 0 0",
                  paddingTop: 22,
                  borderTop: `1px solid ${C.creamBorder}`,
                  fontFamily: mono,
                  fontSize: 13,
                  lineHeight: 1.7,
                  letterSpacing: "0.02em",
                  color: C.gold,
                }}
              >
                {CONTENT.isThisYou.footer}
              </p>
            </div>
            {CONTENT.isThisYou.showMedia && (
              <div className="csp-split-media">
                <MediaFrame label={CONTENT.isThisYou.mediaLabel} dark aspect="4/5" />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 4. HOW THE COURSE IS STRUCTURED. Cream block: three daily-rhythm
          cards (watch → follow → code), a centered footer line, closing on a
          CTA into the pricing grid below. */}
      <section style={{ background: C.cream }}>
        <div className="csp-wrap" style={{ padding: "92px 24px 96px" }}>
          <Kicker>{CONTENT.structure.kicker}</Kicker>
          <h2
            style={{
              margin: "0 0 14px",
              fontFamily: serif,
              fontWeight: 700,
              fontSize: "clamp(32px, 4.6vw, 52px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.05,
              color: C.ink,
            }}
          >
            {CONTENT.structure.heading}
          </h2>
          <p style={{ margin: "0 0 60px", maxWidth: 560, fontSize: 17, lineHeight: 1.65, color: C.muted }}>
            {CONTENT.structure.sub}
          </p>

          {/* Three-card daily rhythm: watch → follow → code. */}
          <div className="csp-cards">
            {CONTENT.structure.cards.map((card) => (
              <div key={card.pill} className="csp-card">
                <span
                  style={{
                    alignSelf: "flex-start",
                    fontFamily: mono,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: C.cream,
                    background: C.navy,
                    padding: "7px 14px",
                    borderRadius: 100,
                    marginBottom: 16,
                  }}
                >
                  {card.pill}
                </span>
                {card.mediaUrl ? (
                  // GIF-style silent loop: autoplay requires muted + playsInline.
                  // Click opens the lightbox below for a full-size view.
                  <video
                    src={card.mediaUrl}
                    aria-label={card.mediaLabel}
                    title="Click to zoom"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    onClick={() => setZoom({ src: card.mediaUrl, label: card.mediaLabel })}
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      boxSizing: "border-box",
                      background: C.navy,
                      cursor: "zoom-in",
                    }}
                  />
                ) : (
                  <MediaPlaceholder label={card.mediaLabel} aspect="4/3" />
                )}
                <p style={{ margin: "16px 0 0", fontFamily: sans, fontSize: 15, lineHeight: 1.7, color: C.muted }}>
                  {card.body}
                </p>
              </div>
            ))}
          </div>

          {/* Footer line, slightly larger than card body copy. */}
          <p style={{ margin: "44px 0 0", textAlign: "center", fontSize: 19, fontWeight: 600, lineHeight: 1.6, color: C.ink }}>
            {CONTENT.structure.footer}
          </p>

          <div style={{ marginTop: 32, textAlign: "center" }}>
            <PrimaryButton color={C.blue}>{CONTENT.structure.cta} ↓</PrimaryButton>
          </div>
        </div>
      </section>

      {/* Video lightbox: backdrop click, the ✕, or Escape closes it. */}
      {zoom && (
        <div
          className="csp-zoom"
          role="dialog"
          aria-modal="true"
          aria-label={zoom.label}
          onClick={() => setZoom(null)}
        >
          <button className="csp-zoom-close" onClick={() => setZoom(null)} aria-label="Close video">
            ✕
          </button>
          <video
            src={zoom.src}
            aria-label={zoom.label}
            autoPlay
            loop
            muted
            playsInline
            controls
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "92vw",
              maxHeight: "86vh",
              display: "block",
              borderRadius: 10,
              background: C.navy,
            }}
          />
        </div>
      )}

      <style>{csp}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bottom of the funnel. CoursesPage renders these after the pricing
   grid: the guarantee removes the last objection right where the
   prices were just seen, then the page closes on one final ask.       */
/* ------------------------------------------------------------------ */

// No Risk Refund Policy: a guarantee panel styled like a certificate, with a
// gold seal breaking the top border. Copy is verbatim from the founder.
export function RefundPolicySection() {
  const { refund } = CONTENT;
  const [before, after] = refund.body.split(refund.email);
  return (
    <section style={{ fontFamily: sans, padding: "26px 0 10px" }}>
      <div style={{ position: "relative", maxWidth: 860, margin: "0 auto", padding: "0 0 6px" }}>
        <div
          style={{
            position: "relative",
            border: `1.5px solid ${C.ink}`,
            borderRadius: 14,
            background: C.white,
            padding: "76px 40px 48px",
            textAlign: "center",
          }}
        >
          {/* Inner dashed rule, certificate style. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 10,
              border: `1px dashed rgba(26,26,46,0.2)`,
              borderRadius: 8,
              pointerEvents: "none",
            }}
          />
          {/* Gold seal breaking the top edge. */}
          <div
            style={{
              position: "absolute",
              top: -37,
              left: "50%",
              transform: "translateX(-50%)",
              width: 74,
              height: 74,
              borderRadius: "50%",
              background: C.gold,
              border: `4px solid ${C.cream}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <span style={{ fontFamily: serif, fontSize: 19, fontWeight: 700, lineHeight: 1 }}>{refund.sealTop}</span>
            <span style={{ fontFamily: mono, fontSize: 7.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 3 }}>
              {refund.sealBottom}
            </span>
          </div>

          <div
            style={{
              fontFamily: mono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: C.gold,
              marginBottom: 14,
            }}
          >
            {refund.kicker}
          </div>
          <h2 style={{ margin: "0 0 22px", fontFamily: serif, fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, letterSpacing: "-0.01em", color: C.ink }}>
            {refund.heading}
          </h2>
          <p style={{ margin: "0 auto", maxWidth: 620, fontSize: 16, lineHeight: 1.8, color: C.muted }}>
            {before}
            <a href={`mailto:${refund.email}`} style={{ color: C.blue, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>
              {refund.email}
            </a>
            {after}
          </p>
        </div>
      </div>
    </section>
  );
}

// Final CTA: a plain anchor back up to the pricing block, with the low-key
// About link underneath so it does not compete.
export function FinalCtaSection() {
  return (
    <section style={{ fontFamily: sans, background: C.navy }}>
      <div className="csp-wrap" style={{ padding: "104px 24px", textAlign: "center" }}>
        <h2 style={{ margin: "0 0 16px", fontFamily: serif, fontWeight: 700, fontSize: "clamp(32px, 5.4vw, 54px)", letterSpacing: "-0.02em", color: "#fff" }}>
          {CONTENT.finalCta.heading}
        </h2>
        <p style={{ margin: "0 0 38px", fontFamily: mono, fontSize: 13, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(245,242,236,0.6)" }}>
          {CONTENT.finalCta.line}
        </p>
        <PrimaryButton color={C.blue}>{CONTENT.finalCta.cta} →</PrimaryButton>
        <div style={{ marginTop: 26 }}>
          <Link
            to="/about"
            className="csp-quiet"
            style={{
              fontFamily: mono,
              fontSize: 12.5,
              letterSpacing: "0.04em",
              color: "rgba(245,242,236,0.5)",
              textDecoration: "underline",
              textUnderlineOffset: 4,
            }}
          >
            {CONTENT.finalCta.aboutLink}
          </Link>
        </div>
      </div>
      <style>{csp}</style>
    </section>
  );
}

const csp = `
  .csp-wrap{max-width:1080px;margin:0 auto;}
  .csp-btn{transition:opacity .2s;}
  .csp-btn:hover{opacity:0.88;}
  .csp-quiet{transition:color .2s;}
  .csp-quiet:hover{color:rgba(245,242,236,0.85);}

  .csp-hero{display:grid;grid-template-columns:1.15fr 1fr;gap:56px;align-items:center;}
  .csp-hero--solo{grid-template-columns:1fr;}

  .csp-fade{animation:csp-up .7s ease both;}
  .csp-fade-2{animation-delay:.15s;}
  @keyframes csp-up{from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:none;}}

  .csp-marquee{overflow:hidden;}
  .csp-marquee-inner{display:flex;width:max-content;animation:csp-scroll 30s linear infinite;}
  @keyframes csp-scroll{from{transform:translateX(0);}to{transform:translateX(-50%);}}

  .csp-dont-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;}
  .csp-dont-card{background:#fff;border:1px solid rgba(26,26,46,0.14);border-radius:12px;padding:26px 26px 24px;transition:transform .25s ease,border-color .25s ease;}
  .csp-dont-grid .csp-dont-card:nth-child(odd){transform:rotate(-0.7deg);}
  .csp-dont-grid .csp-dont-card:nth-child(even){transform:rotate(0.7deg);}
  .csp-dont-card:hover{transform:rotate(0deg);border-color:rgba(26,26,46,0.4);}
  .csp-dont-card--yes{background:#1a1a2e;border-color:#1a1a2e;}
  .csp-dont-card--yes:hover{border-color:#1a1a2e;}

  .csp-split{display:grid;grid-template-columns:1.2fr 1fr;gap:56px;align-items:start;}
  /* Solo checklist: cap at its current split-column width so lines stay short. */
  .csp-split--solo{grid-template-columns:1fr;}
  .csp-split--solo > div:first-child{max-width:560px;}

  .csp-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;align-items:stretch;}
  .csp-card{background:#fff;border:1px solid rgba(26,26,46,0.14);border-radius:12px;padding:22px;display:flex;flex-direction:column;}

  .csp-zoom{position:fixed;inset:0;z-index:1000;background:rgba(26,26,46,0.88);display:flex;align-items:center;justify-content:center;cursor:zoom-out;animation:csp-zoom-in .18s ease;}
  .csp-zoom-close{position:fixed;top:18px;right:22px;width:40px;height:40px;border-radius:50%;border:1px solid rgba(245,242,236,0.35);background:transparent;color:rgba(245,242,236,0.85);font-size:16px;cursor:pointer;transition:background .2s;}
  .csp-zoom-close:hover{background:rgba(245,242,236,0.12);}
  @keyframes csp-zoom-in{from{opacity:0;}to{opacity:1;}}

  @media (prefers-reduced-motion: reduce){
    .csp-marquee-inner{animation:none;}
    .csp-fade,.csp-fade-2{animation:none;}
    .csp-dont-grid .csp-dont-card{transform:none;}
  }

  @media (max-width:920px){
    .csp-hero{grid-template-columns:1fr;gap:44px;padding-top:72px !important;}
    .csp-split{grid-template-columns:1fr;gap:40px;}
    .csp-split-media{max-width:420px;}
    .csp-cards{grid-template-columns:1fr;}
    .csp-dont-grid{grid-template-columns:1fr 1fr;}
  }
  @media (max-width:600px){
    .csp-dont-grid{grid-template-columns:1fr;}
  }
`;
